import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { subscribeToNewsletter } from '../../../services/newsletter/newsletter.api.service';
import SimpleSpinner from '../simple_spinner/simple_spinner';
import './one_click_subscribe_button.scss';

const OneClickSubscribeButton = ({
  newsletter = {},
  showInfoOnSuccess = false,
  isEnabled = false,
}) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showNewsletterInfo, setShowNewsletterInfo] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscription = () => {
    setShowErrorMessage(false);
    setIsLoading(true);

    // params are not needed in the referrer. Ex: http://www.devex.com/newsletters#newswire
    const originAndPathName = window.location.origin + window.location.pathname;
    const referrer = `${originAndPathName}#${newsletter.permalink}`;

    subscribeToNewsletter(newsletter, { referrer })
      .then(() => {
        setShowSuccessMessage(true);
        setShowNewsletterInfo(showInfoOnSuccess);
      })
      .catch(() => {
        setShowErrorMessage(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderIcon = newsletter.icon && (
    <i data-testid='newsletter-icon' className={`icon fal icon-${newsletter.icon}`}></i>
  );

  const renderTitle = newsletter.name && (
    <h3 data-testid='newsletter-title' className='newsletter-title'>
      {newsletter.name}
    </h3>
  );

  const renderDescription = newsletter.description && (
    <p data-testid='newsletter-description' className='newsletter-description text-gray'>
      {newsletter.description}
    </p>
  );

  const alreadySubscribedMessage = (
    <div className='already-subscribed-message' data-testid='already-subscribed-message'>
      <p className='main-title'>You are already subscribed to this newsletter.</p>
      <p className='text-gray-li'>
        You can manage all of your newsletter subscriptions <a href='/account/newsletters'>here</a>.
      </p>
    </div>
  );

  const renderErrorMessage = (
    <p className='error-message small text-red' data-testid='error-message'>
      <i className='fal icon-exclamation-circle'></i>
      <span>Something went wrong. Please try again.</span>
    </p>
  );

  const renderSuccessMessage = (
    <div>
      <p className='success-message text-gray' data-testid='success-message'>
        <span>Thanks for subscribing to the newsletter!</span>
      </p>
      <p className='manage-newsletters text-gray-li'>
        You can manage all of your newsletter subscriptions <a href='/account/newsletters'>here</a>.
      </p>
    </div>
  );

  const subscribeButton = !showSuccessMessage && (
    <button
      id={`subscribe-button-${newsletter.permalink}`}
      data-testid='subscribe-button'
      className='subscribe-btn btn btn-line-primary btn-outline-primary strong'
      disabled={isLoading || !isEnabled}
      onClick={handleSubscription}
    >
      Subscribe now
      <SimpleSpinner display={isLoading} opacity={0.85} />
    </button>
  );

  if (!newsletter.id) return showErrorMessage && renderErrorMessage;

  return (
    <div className='one-click-subscribe-button' data-testid='one-click-subscribe'>
      {renderIcon}
      {showNewsletterInfo && renderTitle}
      {showNewsletterInfo && renderDescription}
      {newsletter.subscribed ? alreadySubscribedMessage : subscribeButton}
      {showSuccessMessage && renderSuccessMessage}
      {showErrorMessage && renderErrorMessage}
    </div>
  );
};

OneClickSubscribeButton.propTypes = {
  newsletter: PropTypes.shape({
    id: PropTypes.number,
    permalink: PropTypes.string,
    icon: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    subscribed: PropTypes.bool,
  }),
  showInfoOnSuccess: PropTypes.bool,
  isEnabled: PropTypes.bool,
};

export default OneClickSubscribeButton;
